import { Component, Input, Output, EventEmitter } from '@angular/core';
import { UpdateUserRequest, DeleteUsersRequest } from 'projects/shared/services/inspection-companies/inspection-company.user.service';
import { UserWithRolesResponse } from 'projects/shared/services/inspection-companies/models/users/user-with-roles.response';
import { UsersDeletedResponse } from 'projects/shared/services/inspection-companies/models/users/users-deleted-response';
import { PaginatedResponse } from 'projects/shared/services/models/paginated.response';
import { BehaviorSubject, Observable } from 'rxjs';
import { FilterCriteria } from './FilterCriteria';
import { CheckUserEvent } from './list-users.component';
import { RoleResponse } from 'projects/shared/services/roles/models/role.response';
import { Permission } from '../../directives/if-permission.directive';

@Component({
  selector: 'shared-list-users-table',
  standalone: false,
  template: `
    <nz-table
      #basicTable
      [nzData]="paginatedData?.data || []"
      [nzPageSize]="(filterCriteria$ | async)?.pageSize || 10"
      [nzPageIndex]="(filterCriteria$ | async)?.page || 1"
      [nzFrontPagination]="false"
      [nzTotal]="paginatedData?.totalItems || 0"
      (nzPageIndexChange)="onPageChange($event)"
      [nzLoading]="isLoading">
      <thead>
        <tr>
          <th></th>
          <th></th>
          <th></th>
          <th></th>
          <th></th>
          <th>{{ 'Portal.InspectionCompanies.Users.CreatedWhen' | translate }}</th>
          <th>{{ 'Portal.InspectionCompanies.Users.CompletedOn' | translate }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let user of basicTable.data">
          <td [nzChecked]="selectedUsers.has(user)" (nzCheckedChange)="onCheckUser($event, user)"></td>
          <td>
            <mac-user-avatar [firstName]="user.firstName" [lastName]="user.lastName" [email]="user.email" />
          </td>
          <td>
            <ng-container *ngIf="!user.firstName && !user.lastName">
              {{ user.email }}
            </ng-container>
            {{ user.firstName }} {{ user.lastName }}
          </td>
          <td>
            <ng-container *ngFor="let role of user.roles; let i = last">
              <label>{{ getRoleLabelKey(role.key) | translate }}</label>
              <label *ngIf="!i">, </label>
            </ng-container>
          </td>
          <td>
            <shared-user-status-badge [status]="user.status" />
          </td>
          <td>
            <ng-container *ngIf="user.createdWhenUtc != undefined">
              {{ user.createdWhenUtc | localizedDate : 'short' }}
            </ng-container>
          </td>
          <td>
            {{ user.completedOnUtc | localizedDate : 'short' }}
          </td>
          <td *ngIf="updateUserAction">
            <span *ngIf="canEditUser(user.status)">
            <shared-edit-user-button
              *ifPermission="editUserPermission"
              [roles$]="roles$"
              [user]="user"
              [deleteUsersAction]="deleteUsersAction"
              [updateUserAction]="updateUserAction"
              (userUpdated)="onUserUpdated()"
              (userDeleted)="onUserDeleted()" /></span>
          </td>
        </tr>
      </tbody>
    </nz-table>
  `,
  styles: [],
})
export class ListUsersTableComponent {
  @Input() paginatedData?: PaginatedResponse<UserWithRolesResponse> | undefined;
  @Input({ required: true }) filterCriteria$: BehaviorSubject<FilterCriteria>;
  @Input({ required: true }) roles$: Observable<RoleResponse[]>;
  @Input() isLoading = false;
  @Input() selectedUsers: Set<UserWithRolesResponse> = new Set<UserWithRolesResponse>();
  @Input() updateUserAction: (userId: number, model: UpdateUserRequest) => Observable<void>;
  @Input() deleteUsersAction: (model: DeleteUsersRequest) => Observable<UsersDeletedResponse>;

  @Output() checkUser = new EventEmitter<CheckUserEvent>();
  @Output() userUpdated = new EventEmitter<void>();
  @Output() userDeleted = new EventEmitter<void>();

  readonly editUserPermission = Permission.UpdateInspectionCompanyUsers
  onPageChange(page: number): void {
    this.filterCriteria$.next({ ...this.filterCriteria$.value, page: page });
  }

  onCheckUser(checked: boolean, user: UserWithRolesResponse): void {
    this.checkUser.emit({ checked, user });
  }

  canEditUser(status: 'Active' | 'Pending'): boolean {
    return status === 'Active';
  }

  getRoleLabelKey(roleKey: string): string {
    return `Domain.Role.${roleKey.toLowerCase()}.Label`;
  }

  onUserUpdated(): void {
    this.userUpdated.emit();
  }

  onUserDeleted(): void {
    this.userDeleted.emit();
  }
}
